import { useEffect, useState } from "react";
import { Download, FileCode2, Printer } from "lucide-react";
import {
  blockQrPayload,
  qrFileName,
  qrPng,
  qrPreview,
  qrSvg,
  saveFile,
  svgDataUrl,
} from "@/lib/blockQr";

/** Inspector QR block: preview + PNG/SVG download + print, bound to the panel type. */
export function BlockQrPanel({ projectCode, name, count }) {
  const [preview, setPreview] = useState(null);
  const payload = blockQrPayload(projectCode, name);

  useEffect(() => {
    setPreview(null);
    qrPreview(payload)
      .then(setPreview)
      .catch((error) => console.debug("QR preview failed", error));
  }, [payload]);

  const print = async () => {
    const svg = await qrSvg(payload);
    const win = window.open("", "_blank", "width=420,height=520");
    if (!win) return;
    win.document.write(
      `<html><head><title>${name}</title><style>
        body{font-family:monospace;text-align:center;padding:24px}
        svg{width:280px;height:280px}
        h1{font-size:18px;margin:16px 0 4px}
        p{font-size:12px;color:#555;margin:0}
      </style></head><body>${svg}<h1>${name}</h1><p>${count} panels</p></body></html>`
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };

  return (
    <div
      data-testid={`inspector-qr-${name}`}
      className="rounded-lg border border-slate-300 bg-white p-3"
    >
      <p className="mono mb-2 text-[9.5px] uppercase tracking-[0.2em] text-slate-500">
        Panel Type QR
      </p>
      <div className="flex items-center gap-3">
        {preview ? (
          <img
            src={preview}
            alt={`QR code for ${name}`}
            data-testid="inspector-qr-preview"
            className="h-20 w-20 rounded border border-slate-300 bg-white p-1"
          />
        ) : (
          <div className="h-20 w-20 rounded border border-slate-300 bg-slate-100" />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <button
            data-testid="inspector-qr-png"
            onClick={async () => saveFile(await qrPng(payload), qrFileName(name, count, "png"))}
            className="mono flex h-7 items-center justify-center gap-1.5 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
          >
            <Download size={11} /> PNG
          </button>
          <button
            data-testid="inspector-qr-svg"
            onClick={async () =>
              saveFile(svgDataUrl(await qrSvg(payload)), qrFileName(name, count, "svg"))
            }
            className="mono flex h-7 items-center justify-center gap-1.5 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
          >
            <FileCode2 size={11} /> SVG
          </button>
          <button
            data-testid="inspector-qr-print"
            onClick={print}
            className="mono flex h-7 items-center justify-center gap-1.5 rounded-full border border-slate-300 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
          >
            <Printer size={11} /> Print
          </button>
        </div>
      </div>
    </div>
  );
}
